import React from 'react';
import {
  isAssociatedEntityRow,
  isLinkedInPersonProfile,
  isLinkedInPeopleSearchRow,
  personInitials,
  personRoleLine,
  buildLinkedInPeopleSearchUrl,
} from './keyPersonsUtils';
import { FEED_CHIP } from '../../components/investigation/feedCardStyles';
import { PIVOT_CTA_CLASS } from '../../components/investigation/TargetPivotsCard';

/**
 * Compact Key Persons grid + LinkedIn People Search + Associated Entities.
 *
 * @param {{
 *   rows: object[],
 *   onPivotToPerson?: Function | null,
 *   companyName?: string,
 *   companyDomain?: string,
 *   locationTags?: string[],
 *   branchLocations?: string[],
 * }} props
 */
export default function KeyPersonsSection({
  rows = [],
  onPivotToPerson = null,
  companyName = '',
  companyDomain = '',
  locationTags = [],
  branchLocations = [],
}) {
  const people = (rows || []).filter(
    (r) =>
      isLinkedInPersonProfile(r) &&
      !isAssociatedEntityRow(r) &&
      !isLinkedInPeopleSearchRow(r),
  );
  const entities = (rows || []).filter(
    (r) => isAssociatedEntityRow(r) && !isLinkedInPeopleSearchRow(r),
  );
  const searchRows = (rows || []).filter(isLinkedInPeopleSearchRow);

  const locTags = Array.isArray(locationTags)
    ? locationTags.map((t) => String(t || '').trim()).filter(Boolean)
    : [];
  const branchTags = Array.isArray(branchLocations)
    ? branchLocations.map((t) => String(t || '').trim()).filter(Boolean)
    : [];
  const locationsForBrowse = locTags.length ? locTags : branchTags;

  const browseFromRow = searchRows[0];
  const browseBuilt = buildLinkedInPeopleSearchUrl({
    companyName: companyName || browseFromRow?.details?.primary_brand || '',
    domain: companyDomain || browseFromRow?.details?.domain || '',
    locations: locationsForBrowse.length
      ? locationsForBrowse
      : Array.isArray(browseFromRow?.details?.locations)
        ? browseFromRow.details.locations
        : [],
  });
  const browseUrl = String(browseFromRow?.url || browseBuilt.url || '').trim();
  const browseKeywords = String(
    browseFromRow?.details?.keywords || browseBuilt.keywords || '',
  ).trim();
  const brandLabel =
    String(companyName || browseFromRow?.details?.primary_brand || '').trim() ||
    String(companyDomain || '').trim() ||
    'Company';
  const locationLabel = locationsForBrowse[0] || '';
  const directoryLabel = locationLabel
    ? `${brandLabel} ${locationLabel}`
    : brandLabel;

  if (!people.length && !entities.length && !browseUrl) return null;

  return (
    <div className="space-y-4" data-testid="key-persons-section">
      {browseUrl ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <a
            href={browseUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex max-w-full items-center gap-1.5 truncate rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-xs font-medium text-cyan-400 transition hover:bg-slate-700/80 hover:text-cyan-300"
            data-testid="linkedin-people-search-link"
            title={browseKeywords || directoryLabel}
          >
            <span aria-hidden>↗</span>
            <span className="truncate">
              Search Directory for &quot;{directoryLabel}&quot;
            </span>
          </a>
        </div>
      ) : null}

      {people.length > 0 ? (
        <div
          className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3"
          data-testid="key-persons-grid"
        >
          {people.map((res, idx) => {
            const name = String(res.target_entity || res.title || 'Person').trim();
            const role = personRoleLine(res, name);
            const initials = personInitials(name);
            const pivotCompany =
              String(companyName || res.details?.primary_brand || '').trim() ||
              String(companyDomain || res.details?.domain || '').trim();

            return (
              <article
                key={`${res.url || name}-${idx}`}
                className="flex min-w-0 flex-col gap-2 rounded-xl border border-teal-500/25 bg-slate-950/50 p-3 shadow-sm"
                data-testid="key-person-card"
              >
                <div className="flex min-w-0 items-start gap-2.5">
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-teal-400/40 bg-teal-950/50 text-[11px] font-bold text-teal-200"
                    aria-hidden
                  >
                    {initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <h4 className="truncate text-sm font-bold text-slate-100" title={name}>
                      {name}
                    </h4>
                    <p className="truncate text-[11px] text-slate-400" title={role}>
                      {role}
                    </p>
                  </div>
                </div>
                <div className="mt-auto flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                  {res.url ? (
                    <a
                      href={res.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-[10px] font-semibold text-cyan-300/90 hover:text-cyan-200"
                    >
                      LinkedIn ↗
                    </a>
                  ) : null}
                  {typeof onPivotToPerson === 'function' && name ? (
                    <button
                      type="button"
                      className={`${PIVOT_CTA_CLASS} sm:ml-auto`}
                      onClick={() =>
                        onPivotToPerson({
                          personName: name,
                          companyName: pivotCompany,
                          domain: String(companyDomain || res.details?.domain || '').trim(),
                          locations: locTags,
                        })
                      }
                    >
                      Pivot to Person
                    </button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div
          className="rounded-xl border border-slate-800 bg-slate-900/40 p-4 text-center text-xs text-slate-400"
          data-testid="key-persons-empty"
        >
          No verified key personnel automatically indexed for this target.
        </div>
      )}

      {entities.length > 0 ? (
        <div
          className="space-y-2 rounded-xl border border-slate-600/50 bg-slate-900/40 p-3"
          data-testid="associated-entities"
        >
          <h4 className="text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
            Associated Entities
          </h4>
          <p className="text-[11px] leading-relaxed text-slate-500">
            Company and institutional pages (not individual profiles).
          </p>
          <ul className="m-0 list-none space-y-2 p-0">
            {entities.map((res, idx) => {
              const label = String(
                res.target_entity || res.title || res.source_entity || 'Entity',
              ).trim();
              return (
                <li
                  key={`${res.url || label}-ent-${idx}`}
                  className="flex min-w-0 items-center justify-between gap-2 rounded-lg border border-white/5 bg-slate-950/50 px-2.5 py-2"
                >
                  <div className="min-w-0">
                    <p className="truncate text-xs font-semibold text-slate-200">{label}</p>
                    {res.url ? (
                      <a
                        href={res.url}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-0.5 block truncate font-mono text-[10px] text-slate-500 hover:text-cyan-300"
                      >
                        {res.url}
                      </a>
                    ) : null}
                  </div>
                  <span className={`${FEED_CHIP} shrink-0 border-slate-500/40 text-slate-300`}>
                    Entity
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
